import { assign, createMachine } from "xstate";
import { Account, AccountHolder } from "@rentablo/api/apiv1";
import { reloadAccounts } from "./services/reloadAccounts";
import { loadAccountHolders } from "./services/loadAccountHolders";
import { addAccountHoldersToContextAction } from "./actions/addAccountHoldersToContextAction";
import {
  batchActivate,
  batchDeactivate,
} from "./services/batchActivateOrDeactivate";
import { addAccountsToContextAction } from "./actions/addAccountsToContextAction";
import { addAxiosErrorToContextAction } from "./actions/addAxiosErrorToContextAction";

export interface NetfondsConsentContext {
  accountHolders: AccountHolder[];
  depots: Account[];
  externalDepotIds: string[];
  error?: ApiException;
}

export type NetfondsConsentEvent =
  | { type: "INIT"; accountHolders: AccountHolder[] }
  | { type: "BATCH_ACTIVATE" }
  | { type: "BATCH_DEACTIVATE" }
  | { type: "ACTIVATE" }
  | { type: "DEACTIVATE" }
  | { type: "DELETE_ACCOUNTS" }
  | { type: "IMPORT_DATA"; depots: Account[]; externalDepotIds?: string[] }
  | { type: "FINISHED" }
  | { type: "BACK" }
  | { type: "SOMETHING_WRONG"; error: AxiosApiError };

type State<T> = { value: T; context: NetfondsConsentContext };

export type NetfondsConsentState =
  | State<"idle">
  | State<"batchActivate">
  | State<"batchDeactivate">
  | State<"activateOrDeactivate">
  | State<"accountHolderSettings">
  | State<"importingData">
  | State<"deleteAccounts">
  | State<"deactivated">
  | State<"backToDashboard">
  | State<"somethingWrong">;

export const netfondsConsentSM = createMachine<
  NetfondsConsentContext,
  NetfondsConsentEvent,
  NetfondsConsentState
>(
  {
    id: "netfondsConsentSM",

    // Initial state
    initial: "idle",

    // Local context for entire machine
    context: {
      accountHolders: [],
      depots: [],
      externalDepotIds: [],
      error: undefined,
    },

    // State definitions
    states: {
      idle: {
        invoke: {
          id: "loadAccountHolders",
          src: loadAccountHolders,
        },
        on: {
          INIT: { target: "activateOrDeactivate" },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      activateOrDeactivate: {
        entry: ["addAccountHoldersToContextAction"],
        on: {
          BATCH_ACTIVATE: {
            target: "batchActivate",
          },
          BATCH_DEACTIVATE: {
            target: "batchDeactivate",
          },
        },
      },
      batchActivate: {
        invoke: {
          id: "batchActivate",
          src: batchActivate,
        },
        on: {
          ACTIVATE: {
            target: "accountHolderSettings",
          },
          DEACTIVATE: {
            target: "deactivated",
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      batchDeactivate: {
        invoke: {
          id: "batchDeactivate",
          src: batchDeactivate,
        },
        on: {
          DEACTIVATE: {
            target: "deactivated",
          },
          DELETE_ACCOUNTS: {
            target: "deleteAccounts",
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      accountHolderSettings: {
        on: {
          IMPORT_DATA: {
            target: "importingData",
          },
          BACK: {
            target: "activateOrDeactivate",
          },
        },
      },
      importingData: {
        entry: ["addAccountsToContextAction"],
        invoke: {
          id: "reloadAccounts",
          src: reloadAccounts,
        },
        on: {
          IMPORT_DATA: {
            target: "importingData",
          },
          FINISHED: {
            target: "backToDashboard",
          },
          SOMETHING_WRONG: { target: "somethingWrong" },
        },
      },
      backToDashboard: {
        type: "final",
      },
      somethingWrong: {
        entry: ["addAxiosErrorToContextAction"],
        type: "final",
      },
      deleteAccounts: {
        on: {
          BACK: {
            target: "activateOrDeactivate",
          },
          DEACTIVATE: {
            target: "deactivated",
          },
        },
      },
      deactivated: {
        on: {
          BACK: {
            target: "idle",
          },
          FINISHED: {
            target: "backToDashboard",
          },
        },
      },
    },
  },
  {
    actions: {
      addAccountHoldersToContextAction: assign(
        addAccountHoldersToContextAction
      ),
      addAccountsToContextAction: assign(addAccountsToContextAction),
      addAxiosErrorToContextAction: assign(addAxiosErrorToContextAction),
    },
  }
);
